import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';
import { cloneDeep, filter, flattenDeep, groupBy, isEqual, isNil, keys, pick, uniqBy, } from 'lodash';
import { Modal } from 'react-bootstrap';
import { titleize, underscored } from 'underscore.string';
import moment from 'moment';
import AccessorialActionCreators from 'actions/AccessorialRequestActionCreators';
import Alert from 'components/ui/Alert';
import BulkAddAccessorialsStore from 'stores/BulkAddAccessorialsStore';
import ToastStore from 'stores/ToastStore';
import { Disclosure } from '@headlessui/react';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import ChevronRightIcon from 'components/v2/icons/ChevronRightIcon';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { getLaneAccessorialRatesV2, getAccessorialRequestsWithStopDetails, } from 'utils/AccessorialRequestWebAPIUtils';
import { getShipmentsWithDetails } from 'utils/ShipmentWebAPIUtils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import Spinner from 'components/ui/Spinner';
import { mapProcessedAccessorialLedgerItem } from 'helpers/accessorials';
import DrayageAccessorialRow from './DrayageAccessorialRow';
import { isVendorChargePaid } from 'common/helpers/accessorials';
import { checkWhichSideAccessorialAutoDeclined, isCarrierMissingInAccessorial, getCarrierAccordionTitle, } from 'helpers/accessorials';
import { sluggify } from 'utils/localization/utils';
import ShipmentLink from 'components/modules/carrier/ShipmentLink';
import ErrorAlert from 'components/ui/ErrorAlert';
const ALERT_MESSAGES = {
    carrier_mismatch: 'ACCESSORIALS_WITH_CARRIER_MISMATCH',
    carrier_missing: 'ACCESSORIALS_WITH_CARRIER_MISSING',
    carrier_mismatch_and_missing: 'ACCESSORIALS_WITH_CARRIER_MISMATCH_AND_MISSING',
    auto_applied_shipper_change_edit: 'AUTO_APPLIED_SHIPPER_CHANGE_EDIT',
};
const META_MAPPINGS = {
    NAME: 'name',
    TYPE: 'type',
    EQUIPMENT: 'equipment',
    SHIPPER_COST: 'shipper_cost',
    CARRIER_COST: 'carrier_cost',
    SHIPPER_UNIT_QTY: 'shipper_unit_qty',
    CARRIER_UNIT_QTY: 'carrier_unit_qty',
    VENDOR: 'vendor',
    NOTES: 'notes',
    DESCRIPTION: 'description',
    CHARGE_CODE: 'charge_code',
    BEGIN_DATETIME: 'begin_datetime',
    END_DATETIME: 'end_datetime',
    LOCATION: 'location',
    REQUIRED_FIELDS: 'required_fields',
};
const REQUIRED_FIELDS_MAP = {
    begin_date_required: 'begin_datetime',
    end_date_required: 'end_datetime',
    location_required: 'location',
    description_required: 'description',
};
const EDIT_FIELDS_MAP = {
    shipper_begin_datetime: { shipper: 'shipper_begin_datetime' },
    begin_datetime: { carrier: 'begin_datetime' },
    shipper_end_datetime: { shipper: 'shipper_end_datetime' },
    end_datetime: { carrier: 'end_datetime' },
    shipper_location: { shipper: 'shipper_location' },
    location: { carrier: 'location' },
    shipper_description: { shipper: 'shipper_description' },
    description: { carrier: 'description' },
    shipper_note: { shipper: 'shipper_note' },
    carrier_note: { carrier: 'carrier_note' },
    carrier_unit_qty: { carrier: 'carrier_unit_qty' },
    shipper_unit_qty: { shipper: 'shipper_unit_qty' },
    shipper_cost: { shipper: 'shipper_rate' },
    carrier_cost: { carrier: 'carrier_rate' },
};
const EDIT_COST_KEY_MAPPINGS = {
    carrier_cost: 'carrier_rate',
    shipper_cost: 'shipper_rate',
};
const MAX_SHIPMENTS_LIMIT = 100;
const Loading = function () {
    return (React.createElement("div", { className: "absolute justify-center w-full", style: { top: '100%' } },
        React.createElement("div", { className: "align-middle" },
            React.createElement("span", { className: "text-5xl text-green" },
                React.createElement(Spinner, { showMessage: false })))));
};
class BulkAddAccessorialsModal extends Component {
    constructor(props) {
        super(props);
        this.fetchAccessorialRequestRate = async (index, self) => {
            const { t } = self.props;
            const shipment = self.state.shipments[index];
            const { carrier, shipper, meta: currentMeta, market } = shipment;
            const { name } = currentMeta;
            let updates = {
                ratesRequestPending: false,
            };
            const query = {
                customer: shipper._id,
                customerType: 'shipper',
                market: market.name,
                status: 'confirmed',
            };
            const shipperRate = await self.getLaneAccessorialRates(query, name, t);
            if (shipperRate && shipperRate.ratetype) {
                if (shipperRate.ratetype === '$') {
                    updates.shipper_cost = shipperRate.rate;
                    updates.receivable_rate_source = 'automated';
                }
                else if (shipperRate.ratetype === '%' && shipment.receivables) {
                    const lineHaulShipper = shipment.receivables.reduce((acc, item) => {
                        return item.category === 'base_cost' ? item.value + acc : acc;
                    }, 0);
                    if (lineHaulShipper > 0 && parseFloat(lineHaulShipper)) {
                        updates.shipper_cost = parseFloat((lineHaulShipper * (shipperRate.rate / 100)).toFixed(2));
                        updates.receivable_rate_source = 'automated';
                    }
                }
            }
            else {
                updates.shipper_cost = 0;
            }
            if (carrier) {
                const carrierQuery = {
                    customer: carrier._id,
                    customerType: 'carrier',
                    market: market.name,
                    status: 'confirmed',
                };
                const carrierRate = await self.getLaneAccessorialRates(carrierQuery, name, t);
                if (carrierRate && carrierRate.ratetype) {
                    if (carrierRate.ratetype === '$') {
                        updates.carrier_cost = carrierRate.rate;
                        updates.payable_rate_source = 'automated';
                    }
                    else if (carrierRate.ratetype === '%' && shipment.payables) {
                        const lineHaulCarrier = shipment.payables.reduce((acc, item) => {
                            return item.category === 'base_cost' ? item.value + acc : acc;
                        }, 0);
                        if (lineHaulCarrier > 0 && parseFloat(lineHaulCarrier)) {
                            updates.carrier_cost = parseFloat((lineHaulCarrier * (carrierRate.rate / 100)).toFixed(2));
                            updates.payable_rate_source = 'automated';
                        }
                    }
                }
                else {
                    updates.carrier_cost = 0;
                }
            }
            self.setState((prevState) => {
                const { shipments: currentShipments } = prevState;
                shipment.meta = Object.assign(Object.assign({}, currentMeta), updates);
                return {
                    shipments: [
                        ...currentShipments.slice(0, index),
                        shipment,
                        ...currentShipments.slice(index + 1),
                    ],
                };
            });
        };
        this.setDrayageAccessorialRowsForEdit = (accessorialsToEdit) => {
            const shipments = accessorialsToEdit.map((acc) => mapShipmentForEdit(acc, this.state.accessorials));
            this.setState({
                shipments,
                accessorialsToEdit,
            });
        };
        this.fetchAccessorials = () => {
            const { t } = this.props;
            return new Promise((resolve) => {
                this.setState({ loadingAccessorials: true }, async () => {
                    try {
                        const result = await getAccessorials({
                            paginate: false,
                        });
                        const { entity: { items }, } = result;
                        const accessorials = items.map((a) => (Object.assign(Object.assign({}, a), { slug: a.slug || underscored(a.name.toLowerCase()) })));
                        this.setState({
                            accessorials,
                            loadingAccessorials: false,
                        }, () => resolve());
                    }
                    catch (e) {
                        ToastStore.addNotification({
                            title: t('LOADING_ACCESSORIALS_ERROR'),
                            body: `${e.message}`,
                            level: 'error',
                        });
                    }
                });
            });
        };
        this.handleCloseStatus = (type) => () => {
            this.setState((currentState) => {
                const { status: currentStatusList } = currentState;
                const status = currentStatusList.filter((s) => s.type !== type);
                return {
                    status,
                };
            });
        };
        this.handleCloseTip = (e) => {
            e.preventDefault();
            this.setState({
                proTipVisible: false,
            });
        };
        this.handleClearAll = () => {
            this.setState((currentState) => {
                const { shipments: currentShipments } = currentState;
                const shipments = currentShipments.map((job) => (Object.assign(Object.assign({}, job), { meta: getDefaultMeta() })));
                return {
                    shipments,
                };
            });
        };
        this.updateRateSource = (meta, field) => {
            if (!field.includes('cost')) {
                return meta;
            }
            meta.rate_source = 'manual';
            if (field === 'carrier_cost') {
                meta.payable_rate_source =
                    (meta === null || meta === void 0 ? void 0 : meta.payable_rate_source) === 'automated' ? 'overwritten' : 'manual';
            }
            if (field === 'shipper_cost') {
                meta.receivable_rate_source =
                    (meta === null || meta === void 0 ? void 0 : meta.receivable_rate_source) === 'automated' ? 'overwritten' : 'manual';
            }
            return meta;
        };
        this.handleUpdateAccessorialRequiredDocs = (bool) => {
            this.setState({
                accessorialRequiredDocsReturned: bool,
            });
        };
        this.handleFieldChange = (index, handleUpdateShipment) => (field, value) => {
            const { shipments } = this.state;
            const currentShipment = shipments[index];
            const currentFieldValue = currentShipment === null || currentShipment === void 0 ? void 0 : currentShipment.meta[field];
            if (value != currentFieldValue) {
                handleUpdateShipment(index, field, value);
            }
        };
        this.handleUpdateShipment = (index, field, value) => {
            try {
                const rateDependencies = ['name'];
                this.setState((prevState) => {
                    const { shipments: currentShipments } = prevState;
                    if (index === -1) {
                        return {
                            shipments: [...currentShipments],
                        };
                    }
                    const shipment = cloneDeep(currentShipments[index]);
                    const isRateDependent = rateDependencies.includes(field);
                    shipment.meta[field] = value;
                    shipment.meta.ratesRequestPending = isRateDependent;
                    shipment.meta = this.updateRateSource(shipment.meta, field);
                    return {
                        fetchAccessorialRates: isRateDependent,
                        shipments: [
                            ...currentShipments.slice(0, index),
                            shipment,
                            ...currentShipments.slice(index + 1),
                        ],
                    };
                }, () => {
                    if (this.state.fetchAccessorialRates) {
                        this.fetchAccessorialRequestRate(index, this);
                    }
                });
            }
            catch (e) {
                console.error(e);
            }
        };
        this.handleUpdateCustomerApproval = (index) => (required, provided) => {
            this.setState((currentState) => {
                var _a, _b;
                const { customerApprovalAccessorials, shipments } = currentState;
                const accessorialInfo = (_b = (_a = shipments[index]) === null || _a === void 0 ? void 0 : _a.meta) === null || _b === void 0 ? void 0 : _b.type;
                const currentCustomerApproval = cloneDeep(customerApprovalAccessorials);
                if (required) {
                    if (!currentCustomerApproval.includes(accessorialInfo)) {
                        currentCustomerApproval.push(accessorialInfo);
                    }
                }
                return {
                    customerApprovalAccessorials: currentCustomerApproval,
                    customerApprovalProvided: provided,
                };
            });
        };
        this.handleVendorFieldChange = (index) => ({ vendor_name, vendor }) => {
            this.setState((prevState) => {
                const { shipments: currentShipments } = prevState;
                if (index === -1) {
                    return {
                        shipments: [...currentShipments],
                    };
                }
                const shipment = cloneDeep(currentShipments[index]);
                shipment.meta.vendor_name = vendor_name;
                shipment.meta.vendor = vendor;
                shipment.meta.ratesRequestPending = !vendor;
                return {
                    shipments: [
                        ...currentShipments.slice(0, index),
                        shipment,
                        ...currentShipments.slice(index + 1),
                    ],
                };
            });
        };
        this.handleRemove = (index) => () => {
            this.setState((currentState) => {
                const { shipments: currentShipments } = currentState;
                return {
                    shipments: [
                        ...currentShipments.slice(0, index),
                        ...currentShipments.slice(index + 1),
                    ],
                };
            });
        };
        this.handleApplyAll = (index) => () => {
            this.setState((prevState) => {
                const { shipments: currentShipments } = prevState;
                const jobIndex = index;
                if (jobIndex === -1) {
                    return {
                        shipments: [...currentShipments],
                    };
                }
                const shipment = cloneDeep(currentShipments[jobIndex]);
                const { meta: targetShipmentMeta } = shipment;
                const updatedShipments = currentShipments.map((job, i) => {
                    // NOTE: don't apply before before the current.
                    if (i < jobIndex) {
                        return Object.assign(Object.assign({}, job), { meta: Object.assign(Object.assign({}, job.meta), { updated: true }) });
                    }
                    // current job meta data
                    let meta = Object.assign({}, job.meta);
                    /*
                     * extract keys from meta date
                     * whose value need to apply rest shipments meta data
                     */
                    const targetShipmentMetaKeys = Object.keys(targetShipmentMeta);
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.LOCATION)) {
                        meta.location = targetShipmentMeta.location;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.BEGIN_DATETIME)) {
                        meta.begin_datetime = targetShipmentMeta.begin_datetime;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.END_DATETIME)) {
                        meta.end_datetime = targetShipmentMeta.end_datetime;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.REQUIRED_FIELDS)) {
                        meta.required_fields = targetShipmentMeta.required_fields;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.NAME)) {
                        meta.name = targetShipmentMeta.name;
                        meta.hasCarrierRate = targetShipmentMeta.hasCarrierRate;
                        meta.hasShipperRate = targetShipmentMeta.hasShipperRate;
                        meta.ratesRequestPending = targetShipmentMeta.ratesRequestPending;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.DESCRIPTION)) {
                        meta.description = targetShipmentMeta.description;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.TYPE)) {
                        meta.type = targetShipmentMeta.type;
                        meta.updated = true;
                    }
                    if (targetShipmentMeta.payee_type) {
                        meta.payee_type = targetShipmentMeta.payee_type;
                        meta.updated = true;
                    }
                    // add equipment to only same shipment.
                    if ((shipment === null || shipment === void 0 ? void 0 : shipment._id) === (job === null || job === void 0 ? void 0 : job._id)) {
                        meta.equipment = targetShipmentMeta.equipment;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.VENDOR)) {
                        meta.vendor = targetShipmentMeta.vendor;
                        meta.vendor_name = targetShipmentMeta.vendor_name;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.SHIPPER_COST)) {
                        meta.shipper_cost = targetShipmentMeta.shipper_cost;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.CARRIER_COST)) {
                        meta.carrier_cost = targetShipmentMeta.carrier_cost;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.SHIPPER_UNIT_QTY)) {
                        meta.shipper_unit_qty = targetShipmentMeta.shipper_unit_qty;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.CARRIER_UNIT_QTY)) {
                        meta.carrier_unit_qty = targetShipmentMeta.carrier_unit_qty;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.NOTES)) {
                        meta.notes = targetShipmentMeta.notes;
                        meta.updated = true;
                    }
                    if (targetShipmentMetaKeys.includes(META_MAPPINGS.CHARGE_CODE)) {
                        meta.charge_code = targetShipmentMeta.charge_code;
                        meta.updated = true;
                    }
                    return Object.assign(Object.assign({}, job), { meta });
                });
                return {
                    shipments: [...updatedShipments],
                };
            });
        };
        this.handleClose = () => {
            this.resetState();
            this.props.onHide();
        };
        this.handleSave = () => {
            const { edit } = this.props;
            let isValid = false;
            this.setState((prevState) => {
                const updatedState = this.validate(prevState);
                isValid = updatedState.saveInProgress;
                return updatedState;
            }, async () => {
                if (isValid && !edit) {
                    this.handleCreateAccessorialRequests();
                }
                if (isValid && edit) {
                    this.handleEditAccessorialRequests();
                }
            });
        };
        this.handleCreateAccessorialRequests = () => {
            const { shipments, accessorials, customerApprovalAccessorials, customerApprovalProvided, } = this.state;
            const requests = shipments.map((shipment) => convertToAccessorialRequest(shipment, accessorials, customerApprovalAccessorials, customerApprovalProvided));
            AccessorialActionCreators.bulkCreateAccessorialRequests(requests);
        };
        this.getRateSource = (originalRateSource, original_rate, updated_rate) => {
            if ((original_rate && updated_rate && original_rate === updated_rate) ||
                originalRateSource === 'overwritten_price_engine')
                return originalRateSource;
            if (originalRateSource === 'automated') {
                return 'overwritten_price_engine';
            }
            return 'manual';
        };
        this.handleEditAccessorialRequests = () => {
            const { shipments, accessorialsToEdit } = this.state;
            const requests = shipments
                .map((shipment) => {
                const originalAccessorial = accessorialsToEdit.find((acc) => acc._id === shipment.meta._id);
                const difference = filter(keys(shipment.meta), (k) => {
                    /**
                     * Why this cost_key check?
                     * Existing code for accessorial row uses carrier_cost and shipper_cost as rate key
                     */
                    const cost_key = EDIT_COST_KEY_MAPPINGS[k];
                    if (cost_key) {
                        return !isEqual(originalAccessorial[cost_key], shipment.meta[k]);
                    }
                    return !isEqual(originalAccessorial[k], shipment.meta[k]);
                });
                const { disable_payer, disable_payee, notes } = shipment.meta;
                if (notes !== originalAccessorial.note)
                    difference.push('notes');
                const validEditFields = pick(EDIT_FIELDS_MAP, difference);
                return keys(validEditFields).reduce((payload, field) => {
                    var _a, _b, _c, _d;
                    payload._id = originalAccessorial._id;
                    if (!disable_payer && ((_a = validEditFields[field]) === null || _a === void 0 ? void 0 : _a.shipper)) {
                        payload[(_b = validEditFields[field]) === null || _b === void 0 ? void 0 : _b.shipper] = shipment.meta[field];
                        payload['shipper_status'] = 'pending-shipper';
                    }
                    if (!disable_payee && ((_c = validEditFields[field]) === null || _c === void 0 ? void 0 : _c.carrier)) {
                        payload[(_d = validEditFields[field]) === null || _d === void 0 ? void 0 : _d.carrier] = shipment.meta[field];
                        payload['carrier_status'] = 'pending-admin';
                    }
                    if (payload.carrier_rate || payload.shipper_rate) {
                        payload['rate_source'] = this.getRateSource(originalAccessorial.rate_source);
                    }
                    if (payload.carrier_rate) {
                        payload['rate_source'] = this.getRateSource(originalAccessorial.rate_source, originalAccessorial.carrier_rate, payload.carrier_rate);
                        payload['payable_rate_source'] = this.getRateSource(originalAccessorial.payable_rate_source, originalAccessorial.carrier_rate, payload.carrier_rate);
                    }
                    if (payload.shipper_rate) {
                        payload['rate_source'] = this.getRateSource(originalAccessorial.rate_source, originalAccessorial.shipper_rate, payload.shipper_rate);
                        payload['receivable_rate_source'] = this.getRateSource(originalAccessorial.receivable_rate_source, originalAccessorial.shipper_rate, payload.shipper_rate);
                    }
                    return payload;
                }, {});
            })
                .filter((item) => item._id);
            AccessorialActionCreators.updateAccessorialException(requests);
            this.handleClose();
        };
        this.handleStoreChange = () => {
            const { t } = this.props;
            const result = BulkAddAccessorialsStore.getBulkAddResult();
            const { successes: successIds, errors } = result;
            this.setState((prevState) => {
                let { shipments, status } = prevState;
                if (successIds.length) {
                    const items = shipments.map((s) => s.shipment_id);
                    status.push({
                        type: 'success',
                        message: t('SHIPMENTS_SUCCESSFULLY_UPDATED'),
                        items,
                    });
                }
                if (successIds.length && !errors.length) {
                    ToastStore.addNotification({
                        title: t('SHIPMENTS_SUCCESSFULLY_UPDATED'),
                        body: '',
                        level: 'success',
                    });
                    this.handleClose();
                }
                if (errors) {
                    const newStatus = [];
                    const getShipmentId = (err) => {
                        return shipments
                            .filter((s) => s.meta.type === err.accessorial_type)
                            .map((s) => s.shipment_id)[0];
                    };
                    errors.forEach((err) => {
                        if (newStatus.some((item) => item.message === err.errMessage)) {
                            const index = newStatus.findIndex((i) => i.message === err.errMessage);
                            const shipmentId = getShipmentId(err);
                            if (!newStatus[index].items.includes(shipmentId)) {
                                newStatus[index].items.push(shipmentId);
                            }
                        }
                        else {
                            newStatus.push({
                                type: 'error',
                                message: err.errMessage || t('SHIPMENTS_UPDATE_FAILURE_TIP'),
                                items: [getShipmentId(err)],
                            });
                        }
                    });
                    status = status.concat(newStatus);
                }
                const updatedShipments = errors.length === 0 ? this.resetShipmentsMeta() : shipments;
                const statusToDisplay = this.transformStatusToDisplay(status);
                return {
                    status: statusToDisplay,
                    saveInProgress: false,
                    shipments: updatedShipments,
                };
            });
        };
        this.handleAddRow = (index) => {
            const { shipments } = this.state;
            const shipment = cloneDeep(shipments[index]);
            if (!shipment || !shipments) {
                return null;
            }
            shipment.meta = getDefaultMeta();
            shipments.push(shipment);
            // Put shipments in order
            const orderedShipments = flattenDeep(Object.values(groupBy(shipments, 'shipment_id')));
            this.setState({ shipments: orderedShipments });
        };
        this.disableSave = () => {
            const { saveInProgress, shipments, loadingAccessorials, accessorialRequiredDocsReturned, } = this.state;
            if (saveInProgress ||
                shipments.length === 0 ||
                loadingAccessorials ||
                (featureEnabled(CONFIGURED_FEATURES.SSR) &&
                    !accessorialRequiredDocsReturned)) {
                return true;
            }
            return false;
        };
        this.state = {
            saveInProgress: false,
            shipments: [],
            proTipVisible: true,
            status: [],
            loadingShipments: false,
            accessorialRequiredDocsReturned: false,
            accessorialsToEdit: [],
            customerApprovalAccessorials: [],
            customerApprovalProvided: false,
            allEquipments: [],
            shipmentsLimitError: false,
        };
    }
    componentDidMount() {
        BulkAddAccessorialsStore.addChangeListener(this.handleStoreChange);
        this.getInitData();
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!this.props.show && nextProps.show) {
            const comparisonResults = compareShipments(this.props.shipments, nextProps.shipments);
            const visibilityChanged = this.props.show !== nextProps.show;
            if (comparisonResults.modified || visibilityChanged) {
                this.mapStateToProps(comparisonResults.shipments);
            }
        }
    }
    componentWillUnmount() {
        BulkAddAccessorialsStore.removeChangeListener(this.handleStoreChange);
    }
    async getInitData() {
        const { edit, selectedAccessorials, shipmentIds, t } = this.props;
        if (shipmentIds.length > MAX_SHIPMENTS_LIMIT) {
            this.setState({ proTipVisible: false, shipmentsLimitError: true });
            return;
        }
        this.setState({ loadingShipments: true }, async () => {
            var _a, _b, _c;
            try {
                await this.fetchAccessorials();
                if (edit && selectedAccessorials.length) {
                    const accessorialsData = await this.getAccessorialRequestsWithStopDetails(shipmentIds, selectedAccessorials);
                    this.setDrayageAccessorialRowsForEdit(accessorialsData);
                }
                else {
                    const result = await getShipmentsWithDetails({
                        _id: shipmentIds,
                        current: [true, false],
                    });
                    if ((_a = result === null || result === void 0 ? void 0 : result.entity) === null || _a === void 0 ? void 0 : _a.success) {
                        const shipments = ((_c = (_b = result === null || result === void 0 ? void 0 : result.entity) === null || _b === void 0 ? void 0 : _b.jobs) === null || _c === void 0 ? void 0 : _c.items) || [];
                        if (shipments === null || shipments === void 0 ? void 0 : shipments.length) {
                            this.mapStateToProps(shipments);
                        }
                    }
                    else {
                        throw new Error('PROBLEM_FETCHING_DATA');
                    }
                }
            }
            catch (e) {
                ToastStore.addNotification({
                    title: t('PROBLEM_FETCHING_DATA'),
                    message: (e === null || e === void 0 ? void 0 : e.message) || e,
                    level: 'error',
                });
            }
            this.setState({ loadingShipments: false });
        });
    }
    async getLaneAccessorialRates(query, type, t) {
        const queryString = new URLSearchParams(query).toString();
        try {
            const results = await getLaneAccessorialRatesV2(queryString);
            const { data: { laneAccessorialRates }, } = results;
            if (!laneAccessorialRates) {
                return null;
            }
            const rate = laneAccessorialRates
                .filter((acc) => acc.accessorialtype)
                .find((r) => r.accessorialtype === type ||
                r.accessorialtype.toLowerCase() === type);
            return rate;
        }
        catch (e) {
            ToastStore.addNotification({
                title: t('PROBLEM_FETCHING_COSTS'),
                message: e,
                level: 'error',
            });
            return null;
        }
    }
    async getAccessorialRequestsWithStopDetails(shipmentIds, accessorialIds) {
        const { t } = this.props;
        try {
            const query = { shipment: shipmentIds, id: accessorialIds };
            const results = await getAccessorialRequestsWithStopDetails(query);
            return results;
        }
        catch (e) {
            ToastStore.addNotification({
                title: t('LOADING_ACCESSORIALS_ERROR'),
                body: '',
                level: 'error',
            });
            throw e;
        }
    }
    mapStateToProps(newShipments) {
        const shipments = newShipments.map((job) => mapShipment(job));
        this.setState({
            shipments,
            saveInProgress: false,
            proTipVisible: true,
            status: [],
        });
    }
    resetShipmentsMeta() {
        const { shipments } = this.state;
        return shipments.map((s) => {
            return Object.assign(Object.assign({}, s), { meta: getDefaultMeta() });
        });
    }
    resetState() {
        this.setState({
            saveInProgress: false,
            shipments: [],
            proTipVisible: true,
        });
    }
    transformStatusToDisplay(status) {
        const { t } = this.props;
        const initValue = [
            {
                type: 'success',
                message: t('SHIPMENTS_SUCCESSFULLY_UPDATED'),
                items: [],
            },
            {
                type: 'error',
                items: [],
            },
        ];
        return status
            .reduce((list, s) => {
            if (s.type === 'success') {
                const oldItems = list[0].items;
                if (!oldItems) {
                    list[0].items = [...s.items];
                }
                list[0].items.push(s);
            }
            if (s.type === 'error') {
                const oldItems = list[1].items;
                if (!oldItems) {
                    list[1].items = [...s.items];
                }
                list[1].items.push(s);
            }
            return list;
        }, initValue)
            .filter((el) => el.items.length);
    }
    validate(currentState) {
        const { shipments: currentShipments } = currentState;
        const { t } = this.props;
        let saveInProgress = false;
        const shipments = currentShipments.map((job) => validateJob(job, t));
        saveInProgress = shipments.every((s) => s.meta.isValid);
        return {
            shipments,
            saveInProgress,
        };
    }
    renderShipmentsLimitErrorInfo() {
        const { t } = this.props;
        const { shipmentsLimitError } = this.state;
        if (!shipmentsLimitError) {
            return null;
        }
        return (React.createElement(ErrorAlert, { title: t('UNABLE_PERFORM_BULK_ACTION'), canClose: false }, `${t('ERROR')}: ${t('SHIPMENT_LIMIT_ERROR_INFO')}`));
    }
    renderProTip() {
        const { t } = this.props;
        let showVendorPaid = false;
        if (!this.state.proTipVisible) {
            return null;
        }
        if (this.state.shipments.some(({ meta: { isVendorPaid } }) => isVendorPaid)) {
            showVendorPaid = true;
        }
        return (React.createElement("div", { className: "flex justify-between w-full p-3 mb-4 border-solid bg-blue-lightest bg-border-t-4 text-blue-dark my-4" },
            React.createElement("div", { className: "flex protip-wrapper" },
                React.createElement("i", { className: "mt-1 mr-3 fa fa-lightbulb-o fa-2x" }),
                React.createElement("div", { className: "flex flex-col mb-0" },
                    React.createElement("strong", { className: "mt-1 mb-2 mr-2" }, t('PRO_TIP_WITH_EXCLAIMATION')),
                    React.createElement("ul", { className: "text-blue" },
                        React.createElement("li", { className: "mb-2" }, t('REMOVE_SHIPMENT_NOT_TO_ADD_ACCESSORIALS')),
                        React.createElement("li", null, t('ACCESSORIALS_NOT_ADDED_IF_CARRIER_FOUND')),
                        React.createElement("li", { className: "mb-2" }, t('FILL_ALL_INFO_BEFORE_APPLY_TO_ALL')),
                        React.createElement("li", { className: "mt-2 text-yellow" }, t('NON_DRAYAGE_NOT_INCLUDES')),
                        showVendorPaid && (React.createElement("li", { className: "mt-2" }, t('SOME_ACCESSORIALS_VENDOR_CHARGES_PAID')))))),
            React.createElement("a", { href: "#", className: "text-lg", onClick: this.handleCloseTip },
                React.createElement("svg", { className: "w-6 h-6 fill-current", role: "button", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 20 20" },
                    React.createElement("title", null, titleize(t('CLOSE'))),
                    React.createElement("path", { d: "M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" })))));
    }
    renderColumnHeaders() {
        const { t, edit } = this.props;
        return (React.createElement("thead", null,
            React.createElement("tr", { className: "font-bold" },
                React.createElement("th", null, titleize(t('SHIPMENT', { count: 1 }))),
                React.createElement("th", null, titleize(t('EQUIPMENT'))),
                React.createElement("th", null, titleize(t('NAME_DESCRIPTION'))),
                React.createElement("th", null, titleize(t('PAYEE_TYPE'))),
                React.createElement("th", null, titleize(t('SHIPPER_RATE'))),
                React.createElement("th", null, titleize(t('UNIT', { count: 2 }))),
                React.createElement("th", null, titleize(t('TOTAL'))),
                React.createElement("th", null, titleize(t('CARRIER_VENDOR_RATE'))),
                React.createElement("th", null, titleize(t('UNIT', { count: 2 }))),
                React.createElement("th", null, titleize(t('TOTAL'))),
                !edit && React.createElement("th", null, titleize(t('NOTE', { count: 2 }))),
                !edit && React.createElement("th", null, titleize(t('APPLY_TO_ALL'))),
                React.createElement("th", null, titleize(t('ACTIONS'))))));
    }
    renderRows() {
        const { t, edit } = this.props;
        const { loadingAccessorials, loadingShipments, shipments, shipmentsLimitError, } = this.state;
        if (loadingAccessorials || loadingShipments) {
            return React.createElement(Loading, null);
        }
        if (shipmentsLimitError) {
            return null;
        }
        if (shipments.length === 0) {
            return (React.createElement("tbody", null,
                React.createElement("tr", null,
                    React.createElement("td", null, t('NO_SHIPMENTS_AVAILABLE_TO_UPDATE')))));
        }
        return (React.createElement("tbody", null, shipments.map((job, index) => {
            return (job.status !== 'job_canceled' && (React.createElement(DrayageAccessorialRow, { index: index, shipment: job, accessorials: this.state.accessorials, onRemove: this.handleRemove(index), onFieldChange: this.handleFieldChange(index, this.handleUpdateShipment), handleUpdateCustomerApproval: this.handleUpdateCustomerApproval(index), onVendorFieldChange: this.handleVendorFieldChange(index), onApplyAll: this.handleApplyAll(index), onRowAdd: () => this.handleAddRow(index), key: index, carrierEnabled: job.carrier, disabledApplyBtn: shipments.length === 1 || index === shipments.length - 1, handleUpdateAccessorialRequiredDocs: this.handleUpdateAccessorialRequiredDocs, edit: edit, defaultAccessorialType: this.props.defaultAccessorialType })));
        })));
    }
    renderErrors() {
        const { shipments } = this.state;
        const { t } = this.props;
        const hasErrors = shipments.find((s) => Object.keys(s.meta.errors).length);
        if (!hasErrors) {
            return null;
        }
        const errors = shipments.reduce((runningList, job) => {
            return runningList.concat(...Object.values(job.meta.errors));
        }, []);
        return (React.createElement(Alert, { type: "error", title: t('CORRECT_ISSUES') },
            React.createElement("ul", null, errors.map((err) => (React.createElement("li", { key: err }, err))))));
    }
    renderStatus(status) {
        const { type, items } = status;
        return (React.createElement(React.Fragment, null, items.map((i) => (React.createElement(Alert, { className: "mb-1.5", type: type, key: type, title: i.message, canClose: true, onClose: this.handleCloseStatus(type) }, i.items.length && (React.createElement("ul", null, i.items.map((id) => (React.createElement("li", { key: id }, id))))))))));
    }
    renderStatuses() {
        const { status } = this.state;
        if (!status || status.length === 0) {
            return null;
        }
        return (React.createElement("div", { className: "flex flex-col mr-4 stop-status-wrapper" }, status.map((s) => this.renderStatus(s))));
    }
    getAlertMessage(alertType) {
        return ALERT_MESSAGES[alertType] || '';
    }
    checkProcessedAccessorials(selectedAccessorials = [], ledger) {
        return selectedAccessorials.filter((selectedAccessorial) => mapProcessedAccessorialLedgerItem(selectedAccessorial, ledger));
    }
    getAccessorialProcessedStatus(accessorial, ledger) {
        var _a;
        const isInProcessedLedger = (_a = this.checkProcessedAccessorials([accessorial], ledger)) === null || _a === void 0 ? void 0 : _a.length;
        if (isInProcessedLedger) {
            return ledger === 'payables' ? 'PAID' : 'INVOICED';
        }
        return ledger === 'payables'
            ? accessorial.carrier_status
            : accessorial.shipper_status;
    }
    renderMismatchAlerts(accessorials = [], shipperSideDeclinedAccessorial = false) {
        const { t } = this.props;
        return accessorials.map(({ meta: acc }) => {
            var _a, _b, _c, _d;
            const payablesStatus = this.getAccessorialProcessedStatus(acc, 'payables');
            const receivablesStatus = this.getAccessorialProcessedStatus(acc, 'receivables');
            return (React.createElement("tr", { key: (_a = acc === null || acc === void 0 ? void 0 : acc.shipment) === null || _a === void 0 ? void 0 : _a._id },
                React.createElement("td", null, t(sluggify(acc === null || acc === void 0 ? void 0 : acc.accessorial_type))),
                React.createElement("td", null,
                    ' ',
                    React.createElement(ShipmentLink, { shipment: acc === null || acc === void 0 ? void 0 : acc.shipment }),
                    ' '),
                React.createElement("td", null, t(sluggify((_b = acc === null || acc === void 0 ? void 0 : acc.shipment) === null || _b === void 0 ? void 0 : _b.status))),
                !shipperSideDeclinedAccessorial && (React.createElement(React.Fragment, null,
                    React.createElement("td", null, ((_c = acc === null || acc === void 0 ? void 0 : acc.shipment) === null || _c === void 0 ? void 0 : _c.carrier_name) || '-'),
                    React.createElement("td", null,
                        ' ',
                        acc[`${acc === null || acc === void 0 ? void 0 : acc.payee_type}_name`]
                            ? `${(_d = acc[`${acc.payee_type}_name`]) !== null && _d !== void 0 ? _d : ''} (${t(sluggify(acc.payee_type))})`
                            : '-'),
                    React.createElement("td", null, t(payablesStatus)),
                    ' ')),
                React.createElement("td", null,
                    ' ',
                    !shipperSideDeclinedAccessorial
                        ? t(receivablesStatus)
                        : t(sluggify(acc === null || acc === void 0 ? void 0 : acc.shipper_decline_reason_code)),
                    ' ')));
        });
    }
    renderMismatchInfo(alertType, accessorials = [], shipperSideDeclinedAccessorial = false) {
        const { t } = this.props;
        const currentCount = accessorials.length;
        const alertClassName = classnames({
            'w-full': true,
            flex: true,
            'justify-between': true,
            'items-center': true,
            'space-x-2': true,
        });
        if (!accessorials || !currentCount)
            return React.createElement("div", null);
        return (React.createElement("div", { className: "w-full mx-auto mb-4" },
            React.createElement(Disclosure, { key: "disclosure" }, ({ open }) => {
                return (React.createElement(React.Fragment, null,
                    React.createElement(Disclosure.Button, { key: "disclosure-button", className: "focus:outline-none focus-visible:ring focus-visible:ring-yellow-500 focus-visible:ring-opacity-75 w-full" },
                        React.createElement(Alert, { type: "warning", className: "w-full" },
                            React.createElement("div", { className: "flex flex-wrap justify-between w-full" }, React.createElement("div", { className: alertClassName },
                                React.createElement("span", { className: "hidden md:inline" }, t(this.getAlertMessage(alertType))),
                                open && (React.createElement(ChevronDownIcon, { className: "font-bold h-6 w-6" })),
                                !open && (React.createElement(ChevronRightIcon, { className: "font-bold h-6 w-6" })))))),
                    React.createElement(Disclosure.Panel, { key: "disclosure-panel", className: "bg-white rounded-md shadow text-sm max-h-80 overflow-y-scroll" }, currentCount > 0 && (React.createElement("table", { className: "default-table" },
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", null, t('ACCESSORIAL')),
                                React.createElement("th", null, t('SHIPMENT_ID')),
                                React.createElement("th", null, t('SHIPMENT_STATUS')),
                                !shipperSideDeclinedAccessorial && (React.createElement(React.Fragment, null,
                                    React.createElement("th", null, t('SHIPMENT_CARRIER')),
                                    React.createElement("th", null, t('ACCESSORIAL_PAYEE')),
                                    React.createElement("th", null, t('PAYEE_STATUS')))),
                                React.createElement("th", null, t('PAYER_STATUS')))),
                        React.createElement("tbody", null, this.renderMismatchAlerts(accessorials, shipperSideDeclinedAccessorial)))))));
            })));
    }
    render() {
        const { show, t, edit } = this.props;
        const { saveInProgress, shipments, loadingAccessorials } = this.state;
        const buttonsDisabled = shipments.length === 0 || saveInProgress || loadingAccessorials;
        const saveDisabled = this.disableSave();
        let carrierMissingInAccessorial = false, carrierMismatchInAccessorial = false;
        const carrierSideDeclinedAccessorial = shipments === null || shipments === void 0 ? void 0 : shipments.filter(({ meta, carrier }) => {
            const carrierSideDeclined = checkWhichSideAccessorialAutoDeclined(meta, 'carrier');
            const carrierMissing = isCarrierMissingInAccessorial(meta);
            const carrierMismatch = (meta === null || meta === void 0 ? void 0 : meta.payee_type) === 'carrier' &&
                (!!carrier || !!(meta === null || meta === void 0 ? void 0 : meta.carrier)) &&
                carrier !== (meta === null || meta === void 0 ? void 0 : meta.carrier);
            if (carrierMissing)
                carrierMissingInAccessorial = true;
            if (carrierMismatch)
                carrierMismatchInAccessorial = true;
            return carrierSideDeclined || carrierMissing || carrierMismatch;
        });
        const carrierErrorTitle = getCarrierAccordionTitle(carrierMissingInAccessorial, carrierMismatchInAccessorial);
        const shipperSideDeclinedAccessorial = shipments.filter(({ meta }) => checkWhichSideAccessorialAutoDeclined(meta, 'shipper'));
        return (React.createElement(Modal, { show: show, onHide: this.handleClose, backdrop: "static", dialogClassName: "modal-override-large bg-grey-modal", keyboard: false },
            React.createElement(Modal.Header, null,
                !edit && (React.createElement(Modal.Title, null, t('ADD_ACCESSORIAL', { count: 3 }))),
                edit && (React.createElement(Modal.Title, null, t('EDIT_ACCESSORIAL', { count: 3 })))),
            React.createElement(Modal.Body, null,
                React.createElement("div", { className: "bg-grey-50", style: { margin: '-15px' } },
                    React.createElement("div", { style: { paddingLeft: '15px', paddingRight: '15px' } },
                        this.renderShipmentsLimitErrorInfo(),
                        this.renderProTip(),
                        !!(edit && (carrierSideDeclinedAccessorial === null || carrierSideDeclinedAccessorial === void 0 ? void 0 : carrierSideDeclinedAccessorial.length)) &&
                            this.renderMismatchInfo(carrierErrorTitle, carrierSideDeclinedAccessorial),
                        !!(edit && (shipperSideDeclinedAccessorial === null || shipperSideDeclinedAccessorial === void 0 ? void 0 : shipperSideDeclinedAccessorial.length)) &&
                            this.renderMismatchInfo('auto_applied_shipper_change_edit', shipperSideDeclinedAccessorial, true),
                        React.createElement("div", { className: "p-4 overflow-x-auto bg-white shadow mt-4" },
                            this.renderErrors(),
                            React.createElement("table", { className: "table" },
                                this.renderColumnHeaders(),
                                this.renderRows()))))),
            React.createElement(Modal.Footer, null,
                React.createElement("div", { className: "flex items-start justify-end" },
                    React.createElement("div", { className: "w-1/2 status-wrapper" }, this.renderStatuses()),
                    React.createElement("div", { className: "form-actions" },
                        React.createElement("button", { className: "mr-2 button button-hollow-green", disabled: buttonsDisabled, onClick: this.handleClearAll }, titleize(t('CLEAR_ALL'))),
                        React.createElement("button", { className: "mr-2 button button-green", disabled: saveDisabled, onClick: this.handleSave },
                            this.state.saveInProgress && (React.createElement("div", { className: "flex items-center" },
                                React.createElement("i", { className: "mr-2 fa fa-circle-o-notch fa-spin" }),
                                React.createElement("span", null,
                                    titleize(t('SAVING')),
                                    "..."))),
                            this.state.loadingShipments && (React.createElement("div", { className: "flex items-center" },
                                React.createElement("i", { className: "mr-2 fa fa-circle-o-notch fa-spin" }),
                                React.createElement("span", null, titleize(t('LOADING'))))),
                            !this.state.saveInProgress && !this.state.loadingShipments && (React.createElement("span", null, titleize(t('SAVE'))))),
                        React.createElement("button", { className: "button secondary", disabled: this.state.saveInProgress, onClick: this.handleClose }, titleize(t('CLOSE'))))))));
    }
}
BulkAddAccessorialsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    edit: PropTypes.bool,
    shipmentIds: PropTypes.array,
    selectedAccessorials: PropTypes.array,
    onHide: PropTypes.func.isRequired,
};
BulkAddAccessorialsModal.defaultProps = {
    shipments: [],
    selectedAccessorials: [],
    edit: false,
};
function compareShipments(currentShipments, nextShipments) {
    const shipmentMap = {};
    currentShipments.forEach((job, index) => {
        shipmentMap[job._id] = {
            index,
            exists: false,
        };
    });
    let hasAdded = false;
    let hasUpdated = false;
    const shipments = nextShipments.map((job) => {
        let updated = true;
        if (shipmentMap[job._id]) {
            const index = shipmentMap[job._id].index;
            updated = !isEqual(job, currentShipments[index]);
            shipmentMap[job._id].exists = true;
            if (updated) {
                hasUpdated = true;
            }
        }
        else {
            hasAdded = true;
        }
        return Object.assign(Object.assign({}, job), { updated });
    });
    const hasRemoved = Object.values(shipmentMap).find((s) => !s.exists) !== undefined;
    const modified = hasAdded || hasUpdated || hasRemoved;
    return {
        modified,
        shipments,
    };
}
function carrierMismatchCheck(accesorial) {
    var _a;
    const { shipment, carrier, payee_type } = accesorial;
    const shipmentCarrier = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.carrier) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.carrier);
    const carrierAssociatedWithAccessorial = (carrier === null || carrier === void 0 ? void 0 : carrier._id) || carrier;
    return (payee_type === 'carrier' &&
        carrierAssociatedWithAccessorial !== shipmentCarrier);
}
function mapShipmentForEdit(accessorial, accessorialList) {
    const shipment = accessorial.shipment;
    const rawEquipmentList = shipment.stops.reduce((runningList, stop) => {
        const { equipments: stopEquipments } = stop;
        return runningList.concat(...stopEquipments);
    }, []);
    const equipments = uniqBy(rawEquipmentList, (e) => e._id);
    const selectedAccessorial = accessorialList.find((acc) => acc.slug === accessorial.accessorial_type &&
        acc.charge_code === accessorial.charge_code);
    const required_fields = Object.keys(REQUIRED_FIELDS_MAP).filter((field) => selectedAccessorial && selectedAccessorial[field]);
    const isVendorPaid = !!isVendorChargePaid(accessorial === null || accessorial === void 0 ? void 0 : accessorial._id, shipment === null || shipment === void 0 ? void 0 : shipment.vendor_payables);
    const { _id, shipment_id, shipper, carrier, order_type, order_oid: orderId, currency, master_account, market, payables, receivables, status, vendor_payables, } = shipment;
    return {
        _id,
        shipment_id,
        equipments,
        shipper,
        carrier,
        vendor_payables,
        meta: Object.assign(Object.assign({}, accessorial), { shipper_cost: accessorial.shipper_rate, carrier_cost: accessorial.carrier_rate, name: accessorial.message, equipment: accessorial.equipment[0], errors: {}, isVendorPaid, type: accessorial.accessorial_type, notes: accessorial.note, required_fields, disable_payee: mapProcessedAccessorialLedgerItem(accessorial, 'payables') ||
                checkWhichSideAccessorialAutoDeclined(accessorial, 'carrier') ||
                isCarrierMissingInAccessorial(accessorial) ||
                carrierMismatchCheck(accessorial), disable_payer: mapProcessedAccessorialLedgerItem(accessorial, 'receivables') ||
                checkWhichSideAccessorialAutoDeclined(accessorial, 'shipper') }),
        order_type,
        orderId,
        currency,
        master_account,
        market,
        payables,
        receivables,
        status,
    };
}
function mapShipment(shipment) {
    const rawEquipmentList = shipment.stops.reduce((runningList, stop) => {
        const { equipments: stopEquipments } = stop;
        return runningList.concat(...stopEquipments);
    }, []);
    const equipments = uniqBy(rawEquipmentList, (e) => e._id);
    const { _id, shipment_id, shipper, carrier, order_type, order_oid: orderId, currency, master_account, market, payables, receivables, status, vendor_payables, timeline, } = shipment;
    return {
        _id,
        shipment_id,
        shipper,
        carrier,
        equipments,
        meta: getDefaultMeta(equipments),
        order_type,
        orderId,
        currency,
        master_account,
        market,
        payables,
        receivables,
        status,
        vendor_payables,
        timeline,
    };
}
function getDefaultMeta(equipments = []) {
    let equipment;
    if (equipments.length === 1) {
        equipment = equipments[0]._id;
    }
    return {
        name: '',
        type: '',
        charge_code: '',
        equipment: equipment || '',
        shipper_cost: '',
        carrier_cost: '',
        shipper_unit_qty: 1,
        carrier_unit_qty: 1,
        notes: '',
        applyAllDisabled: true,
        errors: {},
        isValid: false,
        order_type: '',
        hasCarrierRate: false,
        hasShipperRate: false,
        ratesRequestPending: false,
        payee_type: 'carrier',
        disable_payee: false,
        disable_payer: false,
    };
}
function validateJob(job, t) {
    const { meta: { name, type, equipment, shipper_unit_qty, shipper_cost, carrier_unit_qty, required_fields, begin_datetime, end_datetime, location, vendor, description, vendor_enabled, payee_type, }, order_type, carrier, } = job;
    const errors = {};
    if (order_type !== 'export' && !equipment) {
        errors.equipment = t('SELECT_EQUIPMENT_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
    }
    if (payee_type === 'vendor' && !vendor) {
        errors.vendor = t('SET_VENDOR_WITH_PAYEE_TYPE', {
            shipmentID: job.shipment_id,
        });
    }
    if ((!carrier || vendor_enabled) && payee_type !== 'vendor') {
        errors.payee_type = t('UPDATE_ACCESSORIAL_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
    }
    if (!type) {
        errors.type = t('SET_ACCESSORIAL_TYPE_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
    }
    if (!name) {
        errors.name = t('SET_ACCESSORIAL_NAME_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
    }
    if (!shipper_unit_qty && !carrier_unit_qty) {
        errors.shipper_unit_qty = t('SHIPPER_UNITS_AMOUNT_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
        errors.carrier_unit_qty = t('CARRIER_UNITS_AMOUNT_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
    }
    let parsedShipperCost = parseFloat(shipper_cost);
    if (isNil(parsedShipperCost) || parsedShipperCost < 0) {
        errors.shipper_cost = t('PAYER_RATE_GREATER_THAN_ZERO_WITH_VALUE', {
            shipmentID: job.shipment_id,
        });
    }
    if (type && type !== 'wait_time') {
        if (!parseFloat(shipper_unit_qty) && !parseFloat(carrier_unit_qty)) {
            errors.shipper_unit_qty = t('INTEGER_SHIPPER_UNITS_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
            errors.carrier_unit_qty = t('INTEGER_CARRIER_UNITS_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
    }
    if (type && type === 'wait_time') {
        if (!parseFloat(shipper_unit_qty)) {
            errors.shipper_unit_qty = t('DECIMAL_SHIPPER_UNITS_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
        if (!parseFloat(carrier_unit_qty)) {
            errors.carrier_unit_qty = t('DECIMAL_CARRIER_UNITS_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
    }
    if (required_fields && required_fields.length) {
        if (required_fields.includes('begin_date_required') && !begin_datetime) {
            errors.begin_date_required = t('BEGIN_TIME_REQUIRED_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
        if (required_fields.includes('end_date_required') && !end_datetime) {
            errors.end_date_required = t('END_TIME_REQUIRED_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
        if (required_fields.includes('location_required') && !location) {
            errors.location_required = t('LOCATION_REQUIRED_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
        if (required_fields.includes('description_required') && !description) {
            errors.description_required = t('DESCRIPTION_REQUIRED_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
        if (required_fields.includes('end_date_required') &&
            required_fields.includes('begin_date_required') &&
            end_datetime &&
            begin_datetime &&
            (moment(begin_datetime).isAfter(end_datetime) ||
                moment(begin_datetime).isSame(end_datetime))) {
            errors.date_inconsistency = t('TIME_NOT_SAME_OR_BEFORE_WITH_VALUE', {
                shipmentID: job.shipment_id,
            });
        }
    }
    const isValid = Object.keys(errors).length === 0;
    return Object.assign(Object.assign({}, job), { meta: Object.assign(Object.assign({}, job.meta), { errors,
            isValid }) });
}
function getEquipment(equipmentId, equipments) {
    const equipment = equipments.find((eq) => eq._id.toString() === equipmentId);
    if (equipment) {
        return [equipment];
    }
    return equipments;
}
function convertToAccessorialRequest(shipment, accessorials, customerApprovalAccessorials, customerApprovalProvided) {
    var _a, _b;
    const { meta, equipments, status } = shipment;
    const accessorial = accessorials.find((accessorial) => accessorial.name === meta.name);
    const request = {
        type: 'accessorial',
        exception_type: 'Accessorial Request',
        carrier_status: 'pending-admin',
        shipper_status: 'pending-shipper',
        shipment: shipment._id,
        shipper: shipment.shipper
            ? shipment.shipper._id || shipment.shipper
            : shipment.shipper,
        carrier: shipment.carrier
            ? shipment.carrier._id || shipment.carrier
            : shipment.carrier,
        accessorial_type: meta.type,
        message: meta.name,
        charge_code: meta.charge_code,
        shipment_id: shipment.shipment_id,
        shipper_name: shipment.shipper_name,
        carrier_name: shipment.carrier_name,
        begin_datetime: meta.begin_datetime,
        end_datetime: meta.end_datetime,
        vendor: meta.vendor,
        vendor_name: meta.vendor_name,
        payee_type: meta.payee_type,
        location: meta.location,
        description: meta.description,
        carrier_rate: parseFloat(meta.carrier_cost),
        carrier_unit_qty: parseFloat(meta.carrier_unit_qty) || 1,
        shipper_rate: parseFloat(meta.shipper_cost),
        shipper_unit_qty: parseFloat(meta.shipper_unit_qty) || 1,
        equipment: (meta === null || meta === void 0 ? void 0 : meta.equipment)
            ? getEquipment(meta.equipment, equipments)
            : equipments,
        note: meta.notes,
        shipper_note: meta.notes,
        carrier_note: meta.notes,
        meta,
        days_before_auto_decline: accessorial.days_before_auto_decline,
        anchor_event: accessorial.anchor_event,
        master_shipper: shipment.master_account ? shipment.master_account.id : null,
        status,
        rate_source: meta.rate_source || 'manual',
        receivable_rate_source: meta.receivable_rate_source || 'manual',
        payable_rate_source: meta.payable_rate_source || 'manual',
    };
    if (customerApprovalAccessorials.includes(meta.type)) {
        request.customer_approval_provided = customerApprovalProvided;
    }
    if ((_b = (_a = shipment === null || shipment === void 0 ? void 0 : shipment.timeline) === null || _a === void 0 ? void 0 : _a.job_closed) === null || _b === void 0 ? void 0 : _b.at)
        request.job_closed_date = shipment.timeline.job_closed.at;
    return request;
}
export default withTranslation()(BulkAddAccessorialsModal);
